import { getAuthToken } from '../../services/auth/tokenStorage';
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  Modal,
  Pressable,
  ScrollView,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { NavigationProp, useFocusEffect } from '@react-navigation/native';
import { launchCamera, launchImageLibrary, ImagePickerResponse, CameraOptions, ImageLibraryOptions } from 'react-native-image-picker';
import UserImageCircle from '../../components/UserImageCircle';
import { onboardingService, OnboardingProgress } from '../../services/onboarding';
import { API_URL } from '../../config';
import { profileLogger } from '../../utils/logger';
import { getCachedProfileImage, removeCachedProfileImage, setCachedProfileImage } from '../../services/profileImageCache';

// Import default profile image
import defaultProfileImage from '../../assets/icons/profile.png';

// Import icons
import backIcon from '../../assets/icons/back.png';
import lockIcon from '../../assets/icons/lock.png';
import trashIcon from '../../assets/icons/trash.png';
import languageIcon from '../../assets/icons/language.png';
import notificationsIcon from '../../assets/icons/notifications.png';
import chevronIcon from '../../assets/icons/chevron.png';
import editIcon from '../../assets/icons/edit.png';
import cameraIcon from '../../assets/icons/camera.png';
import galleryIcon from '../../assets/icons/gallery.png';
import billingIcon from '../../assets/icons/allExpenses.png';
import legalIcon from '../../assets/icons/legal.png';
import infoIcon from '../../assets/icons/info.png';
import userIcon from '../../assets/icons/user.png';
import calendarIcon from '../../assets/icons/calendar.png';
import friendsIcon from '../../assets/icons/friends.png';
import familyIcon from '../../assets/icons/family.png';

interface SettingsScreenProps {
  navigation: NavigationProp<any>;
}

const SettingsScreen: React.FC<SettingsScreenProps> = ({ navigation }) => {
  const [showPhotoOptions, setShowPhotoOptions] = useState(false);
  const [onboardingProgress, setOnboardingProgress] = useState<OnboardingProgress | null>(null);
  const [profileImageUri, setProfileImageUri] = useState<string | null>(null);

  // Load profile image when screen focuses
  useFocusEffect(
    React.useCallback(() => {
      loadProfileImage();
      checkOnboardingStatus();
    }, [])
  );

  // Convert image URI to base64 data URI
  const convertImageToBase64 = async (uri: string): Promise<string | null> => {
    try {
      const response = await fetch(uri);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve(reader.result as string);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      profileLogger.error('Error converting image to base64:', error);
      return null;
    }
  };

  const loadProfileImage = async () => {
    try {
      const token = await getAuthToken();

      // Try to load from backend first
      if (token) {
        try {
          const response = await fetch(`${API_URL}/users/profile-image`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });

          if (response.ok) {
            const data = await response.json();
            if (data.success && data.imageData) {
              await setCachedProfileImage(data.imageData);
              setProfileImageUri(data.imageData);
              return;
            }

            if (data.success) {
              await removeCachedProfileImage();
              setProfileImageUri(null);
              return;
            }
          }
        } catch (backendError) {
          profileLogger.debug('Could not load from backend, trying local cache:', backendError);
        }
      }

      const savedImageUri = await getCachedProfileImage();
      setProfileImageUri(savedImageUri);
    } catch (error) {
      profileLogger.error('Error loading profile image:', error);
    }
  };

  const saveProfileImage = async (uri: string) => {
    try {
      // Convert image URI to base64
      const base64Data = await convertImageToBase64(uri);
      if (!base64Data) {
        Alert.alert('Error', 'Failed to process image');
        return;
      }

      // Save to local cache immediately for quick display
      await setCachedProfileImage(base64Data);
      setProfileImageUri(base64Data);

      // Upload to backend
      const token = await getAuthToken();
      if (token) {
        try {
          const response = await fetch(`${API_URL}/users/profile-image`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ imageData: base64Data }),
          });

          if (!response.ok) {
            profileLogger.warn('Backend profile image upload failed, but local cache saved');
          }
        } catch (backendError) {
          profileLogger.warn('Could not upload to backend, but local cache saved:', backendError);
        }
      }
    } catch (error) {
      profileLogger.error('Error saving profile image:', error);
    }
  };

  const removeProfileImage = async () => {
    try {
      // Remove from local cache
      await removeCachedProfileImage();
      setProfileImageUri(null);
      setShowPhotoOptions(false);

      // Remove from backend
      const token = await getAuthToken();
      if (token) {
        try {
          await fetch(`${API_URL}/users/profile-image`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
        } catch (backendError) {
          profileLogger.warn('Could not delete from backend:', backendError);
        }
      }

      Alert.alert('Success', 'Profile photo removed');
    } catch (error) {
      profileLogger.error('Error removing profile image:', error);
      Alert.alert('Error', 'Failed to remove profile photo');
    }
  };

  const handleImageResponse = (response: ImagePickerResponse) => {
    setShowPhotoOptions(false);

    if (response.didCancel) {
      profileLogger.debug('User cancelled image picker');
      return;
    }

    if (response.errorCode) {
      profileLogger.error('ImagePicker Error:', response.errorMessage);
      Alert.alert('Error', response.errorMessage || 'Failed to select image');
      return;
    }

    if (response.assets && response.assets.length > 0) {
      const selectedImage = response.assets[0];
      if (selectedImage.uri) {
        saveProfileImage(selectedImage.uri);
        Alert.alert('Success', 'Profile photo updated');
      }
    }
  };

  const handleCameraPress = async () => {
    const options: CameraOptions = {
      mediaType: 'photo',
      quality: 0.8,
      maxWidth: 500,
      maxHeight: 500,
      saveToPhotos: false,
      cameraType: 'front',
    };

    try {
      const result = await launchCamera(options);
      handleImageResponse(result);
    } catch (error) {
      profileLogger.error('Camera error:', error);
      Alert.alert('Error', 'Failed to open camera. Please check camera permissions.');
    }
  };

  const handleGalleryPress = async () => {
    const options: ImageLibraryOptions = {
      mediaType: 'photo',
      quality: 0.8,
      maxWidth: 500,
      maxHeight: 500,
      selectionLimit: 1,
    };

    try {
      const result = await launchImageLibrary(options);
      handleImageResponse(result);
    } catch (error) {
      profileLogger.error('Gallery error:', error);
      Alert.alert('Error', 'Failed to open gallery. Please check photo library permissions.');
    }
  };

  const checkOnboardingStatus = async () => {
    try {
      const progress = await onboardingService.getProgress();
      setOnboardingProgress(progress);
    } catch (error) {
      profileLogger.error('Failed to fetch onboarding progress:', error);
    }
  };

  // Render profile image - show selected image, or default profile.png
  const renderProfileImage = () => {
    if (profileImageUri) {
      return (
        <Image
          source={{ uri: profileImageUri }}
          style={styles.profileImage}
        />
      );
    }
    return (
      <Image
        source={defaultProfileImage}
        style={styles.profileImage}
      />
    );
  };

  const handleCompleteOnboarding = () => {
    if (!onboardingProgress) {
      // No progress yet, start fresh
      navigation.navigate('Auth', { screen: 'AIOnboardingWelcome' });
    } else if (onboardingProgress.isSkipped) {
      // Was skipped, go to welcome screen to start fresh
      navigation.navigate('Auth', { screen: 'AIOnboardingWelcome' });
    } else if (onboardingProgress.isPaused) {
      // Paused, go to paused screen
      navigation.navigate('Auth', { screen: 'AIOnboardingPaused' });
    } else {
      // In progress, resume
      navigation.navigate('Auth', { screen: 'AIOnboardingChat', params: { resume: true } });
    }
  };

  const baseMenuItems = [
    {
      title: 'My Profile',
      icon: userIcon,
      onPress: () => navigation.navigate('ProfileDetails'),
      iconColor: '#29BFC0',
    },
    {
      title: 'Change Password',
      icon: lockIcon,
      onPress: () => navigation.navigate('ChangePassword'),
      iconColor: '#29BFC0',
    },
    {
      title: 'Billing',
      icon: billingIcon,
      onPress: () => navigation.navigate('SubscriptionManagement'),
      iconColor: '#29BFC0',
    },
    {
      title: 'Refer a Friend',
      icon: friendsIcon,
      onPress: () => navigation.navigate('ReferAFriend'),
      iconColor: '#29BFC0',
    },
    {
      title: 'Family Plan',
      icon: familyIcon,
      onPress: () => navigation.navigate('FamilyPlan'),
      iconColor: '#29BFC0',
    },
    {
      title: 'Manage Access',
      icon: lockIcon,
      onPress: () => navigation.navigate('AccessChecklist'),
      iconColor: '#29BFC0',
    },
    {
      title: 'Auto-Lock Timer',
      icon: lockIcon,
      onPress: () => navigation.navigate('IdleLockSettings'),
      iconColor: '#29BFC0',
    },
    {
      title: 'Recovery Code',
      icon: lockIcon,
      onPress: () => navigation.navigate('RecoveryCodeSettings'),
      iconColor: '#29BFC0',
    },
    {
      title: 'Two-Factor Authentication',
      icon: lockIcon,
      onPress: () =>
        navigation.navigate('LetterFirst', {
          screen: 'SecurityOptions',
          params: { from: 'settings' },
        }),
      iconColor: '#29BFC0',
    },
    {
      title: 'EOL Access Policy',
      icon: calendarIcon,
      onPress: () => navigation.navigate('DeathAccessPolicy'),
      iconColor: '#29BFC0',
    },
    {
      title: 'Secret Code Recipients',
      icon: userIcon,
      onPress: () => navigation.navigate('SecretCodeRecipients'),
      iconColor: '#29BFC0',
    },
  ];

  // Conditionally add "Complete Onboarding" or "Start Onboarding" based on status
  // Show if: progress exists and not completed, OR progress exists and was skipped
  const shouldShowOnboarding = onboardingProgress && (!onboardingProgress.isCompleted || onboardingProgress.isSkipped);
  const onboardingItem = shouldShowOnboarding ? {
    title: onboardingProgress.isSkipped ? 'Start Onboarding' : 'Complete Onboarding',
    icon: infoIcon,
    onPress: handleCompleteOnboarding,
    iconColor: '#F2994A',
  } : null;

  const remainingMenuItems = [
    {
      title: 'Notifications',
      icon: notificationsIcon,
      onPress: () => navigation.navigate('Notifications'),
      iconColor: '#29BFC0',
    },
    {
      title: 'Delete Data',
      icon: trashIcon,
      onPress: () => navigation.navigate('DeleteData'),
      iconColor: '#E85C5C',
    },
    {
      title: 'Delete Account',
      icon: trashIcon,
      onPress: () => navigation.navigate('DeleteAccount'),
      iconColor: '#E85C5C',
    },
    {
      title: 'Language Settings',
      icon: languageIcon,
      onPress: () => navigation.navigate('LanguageSettings'),
      iconColor: '#29BFC0',
    },
    {
      title: 'Privacy Policy',
      icon: legalIcon,
      onPress: () => navigation.navigate('PrivacyPolicy'),
      iconColor: '#29BFC0',
    },
    {
      title: 'Terms of Service',
      icon: infoIcon,
      onPress: () => navigation.navigate('TermsOfService'),
      iconColor: '#29BFC0',
    },
    {
      title: 'About FinalKey',
      icon: infoIcon,
      onPress: () => navigation.navigate('About'),
      iconColor: '#29BFC0',
    },
  ];

  // Combine all menu items, filtering out null values
  const menuItems = [
    ...baseMenuItems,
    onboardingItem,
    ...remainingMenuItems,
  ].filter(Boolean);

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()}>
          <Image source={backIcon} style={styles.headerIcon} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Settings</Text>
        <View style={styles.headerPlaceholder} />
      </View>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.profileSection}>
          <View style={styles.profileImageContainer}>
            <UserImageCircle size={80}>
              {renderProfileImage()}
            </UserImageCircle>
            <TouchableOpacity
              style={styles.editImageButton}
              onPress={() => setShowPhotoOptions(true)}
            >
              <Image source={editIcon} style={styles.editIcon} />
            </TouchableOpacity>
          </View>
        </View>

        <View style={styles.menuContainer}>
          {menuItems.map((item, index) => (
            <TouchableOpacity
              key={index}
              style={styles.menuItem}
              onPress={item.onPress}
            >
              <View style={styles.menuItemLeft}>
                <Image
                  source={item.icon}
                  style={[styles.menuItemIcon, { tintColor: item.iconColor }]}
                />
                <Text style={styles.menuItemText}>{item.title}</Text>
              </View>
              <Image source={chevronIcon} style={styles.menuItemArrow} />
            </TouchableOpacity>
          ))}
        </View>
      </ScrollView>

      <Modal
        visible={showPhotoOptions}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowPhotoOptions(false)}
      >
        <Pressable
          style={styles.modalOverlay}
          onPress={() => setShowPhotoOptions(false)}
        >
          <View style={styles.photoOptionsContainer}>
            <Text style={styles.photoOptionsTitle}>Update New Profile Picture</Text>
            <Text style={styles.photoOptionsSubtitle}>
              Here are the recent photos to look, start choosing one of them
            </Text>
            <View style={styles.photoOptions}>
              <TouchableOpacity
                style={styles.photoOption}
                onPress={handleCameraPress}
              >
                <Image source={cameraIcon} style={[styles.optionIcon, { tintColor: '#29BFC0' }]} />
                <Text style={styles.photoOptionText}>Camera</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.photoOption}
                onPress={handleGalleryPress}
              >
                <Image source={galleryIcon} style={[styles.optionIcon, { tintColor: '#29BFC0' }]} />
                <Text style={styles.photoOptionText}>Gallery</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.photoOption}
                onPress={removeProfileImage}
              >
                <Image source={trashIcon} style={[styles.optionIcon, { tintColor: '#E85C5C' }]} />
                <Text style={[styles.photoOptionText, { color: '#E85C5C' }]}>
                  Remove Photo
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </Pressable>
      </Modal>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: ms(20),
    paddingVertical: ms(15),
    marginTop: ms(10),
    backgroundColor: '#F3F6F8',
  },
  headerTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#333',
  },
  headerIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  headerPlaceholder: {
    width: s(24),
    height: s(24),
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: ms(20),
    paddingBottom: ms(30),
  },
  profileSection: {
    alignItems: 'center',
    marginVertical: ms(15),
  },
  profileImageContainer: {
    position: 'relative',
  },
  profileImage: {
    width: s(80),
    height: s(80),
    borderRadius: ms(40),
  },
  editImageButton: {
    position: 'absolute',
    right: ms(-5),
    bottom: ms(-5),
    backgroundColor: '#FFFFFF',
    width: s(28),
    height: s(28),
    borderRadius: ms(14),
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: ms(1),
    borderColor: '#29BFC0',
  },
  editIcon: {
    width: s(16),
    height: s(16),
    tintColor: '#29BFC0',
  },
  menuContainer: {
    marginTop: ms(10),
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#FFFFFF',
    padding: ms(15),
    borderRadius: ms(50),
    marginBottom: ms(15),
    paddingHorizontal: ms(20),
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  menuItemLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  menuItemIcon: {
    width: s(24),
    height: s(24),
    marginRight: ms(15),
  },
  menuItemText: {
    fontSize: ms(16, 0.3),
    color: '#333333',
  },
  menuItemArrow: {
    width: s(24),
    height: s(24),
    tintColor: '#333333',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  photoOptionsContainer: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    padding: ms(20),
  },
  photoOptionsTitle: {
    fontSize: ms(20, 0.3),
    fontWeight: 'bold',
    color: '#333',
    marginBottom: ms(10),
    textAlign: 'center',
  },
  photoOptionsSubtitle: {
    fontSize: ms(16, 0.3),
    color: '#666',
    textAlign: 'center',
    marginBottom: ms(20),
  },
  photoOptions: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    paddingVertical: ms(20),
  },
  photoOption: {
    alignItems: 'center',
  },
  photoOptionText: {
    marginTop: ms(8),
    fontSize: ms(14, 0.3),
    color: '#333',
  },
  optionIcon: {
    width: s(24),
    height: s(24),
  },
});

export default SettingsScreen;
